import { type PageLink, PageTitle } from '@/_metronic/layout/core';
import { type ReactNode, useEffect } from 'react';
import QueryErrorBoundary from '../feedback/QueryErrorBoundary';
import { useInterfaceAppearance } from '@/app/modules/interface-appearance/useInterfaceAppearance';

type PageShellProps = {
  title: string;
  breadcrumbs?: Array<PageLink>;
  toolbar?: ReactNode;
  /** Container sizing/layout classes appended to `app-container`. */
  containerClassName?: string;
  children: ReactNode;
};

/**
 * Standard page chrome: Metronic PageTitle + breadcrumbs, browser tab title
 * sync and the app container wrapper. Every route-target page should render
 * its content inside a PageShell instead of repeating this boilerplate.
 */
const PageShell = ({ title, breadcrumbs, toolbar, containerClassName = 'container-xxl', children }: PageShellProps) => {
  const { appearance } = useInterfaceAppearance();
  const systemName = appearance.system_name;
  useEffect(() => {
    const previousTitle = document.title;
    const previousTitleIsDefault = previousTitle.endsWith(' — Backoffice');
    const restoredTitle = previousTitleIsDefault ? `${systemName} — Backoffice` : previousTitle;
    document.title = `${title} — ${systemName}`;
    return () => {
      document.title = restoredTitle;
    };
  }, [title, systemName]);

  return (
    <div className={`app-container ${containerClassName}`}>
      <PageTitle breadcrumbs={breadcrumbs}>{title}</PageTitle>
      {toolbar ? <div className="d-flex justify-content-end mb-5">{toolbar}</div> : null}
      <QueryErrorBoundary>{children}</QueryErrorBoundary>
    </div>
  );
};

export default PageShell;
